import React, { useEffect, useRef, useState } from 'react';
import './NewHeroHeader.css';

const testimonials = [
    {
        quote: 'I was initially hesitant about buying a refurbished laptop, but Nexawave completely changed my perspective. The Dell Latitude I received looks and works exactly like a brand new device. The battery life is excellent, all ports work perfectly, and I saved almost 60% compared to buying new. The 6-month warranty gave me confidence.',
        name: 'Keerthi Reddy',
        role: 'Madhapur, Hyderabad',
        product: 'Dell Latitude E7440',
        avatar: 'https://randomuser.me/api/portraits/women/44.jpg',
        rotation: '-rotate-1'
    },
    {
        quote: 'Absolutely impressed with the quality and service! I ordered a MacBook Air M1 and it arrived within 3 days, perfectly packaged. The device had no scratches, the screen was pristine, and it came with the original charger. I have been using it for my digital marketing work for 4 months now, and it handles everything smoothly.',
        name: 'Syed Ibrahim',
        role: 'Gachibowli, Hyderabad',
        product: 'MacBook Air M1',
        avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
        rotation: 'rotate-1'
    },
    {
        quote: 'As a graphic designer, I needed a powerful laptop that could handle Adobe Creative Suite without breaking the bank. The ASUS ROG Strix I purchased from Nexawave has been perfect for my needs. The graphics performance is excellent, and I can work on complex designs without any lag. Great value for creative professionals.',
        name: 'Ananya Rao',
        role: 'Indiranagar, Bangalore',
        product: 'ASUS ROG Strix',
        avatar: 'https://randomuser.me/api/portraits/women/68.jpg',
        rotation: '-rotate-2'
    },
    {
        quote: 'This was my second purchase from Nexawave, and they continue to exceed expectations. The Mac Mini I bought works flawlessly for my business analysis work. What impressed me most was their transparent grading system - they clearly mentioned the condition, and the device was exactly as described. Highly recommended.',
        name: 'Mohammed Tariq',
        role: 'Andheri, Mumbai',
        product: 'Apple Mac Mini',
        avatar: 'https://randomuser.me/api/portraits/men/46.jpg',
        rotation: 'rotate-2'
    }
];

export default function TestimonialsSection() {
    const railRef = useRef(null);
    const sectionRef = useRef(null);
    const headingRef = useRef(null);
    const [headingVisible, setHeadingVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('animate');
                    }
                });
            },
            { rootMargin: '0px 0px -60px 0px', threshold: 0.08 }
        );
        const els = sectionRef.current?.querySelectorAll('.animate-on-scroll');
        if (els) {
            els.forEach((el) => observer.observe(el));
        }
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setHeadingVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.15 }
        );
        if (headingRef.current) observer.observe(headingRef.current);
        return () => observer.disconnect();
    }, []);

    const splitWords = (text, startDelay = 0) => {
        const words = text.trim().split(/\s+/);
        return words.map((word, i) => (
            <span
                key={`${word}-${i}`}
                className="word-animate"
                style={{ animationDelay: `${startDelay + i * 40}ms` }}
            >
                {word}
                {i < words.length - 1 ? ' ' : ''}
            </span>
        ));
    };

    const scrollRail = (dir) => {
        if (railRef.current) {
            // Improved scrolling distance logic for mobile
            const cardWidth = window.innerWidth < 640 ? window.innerWidth * 0.85 + 20 : 460;
            railRef.current.scrollBy({ left: dir * cardWidth, behavior: 'smooth' });
        }
    };

    return (
        <section ref={sectionRef} id="reviews" className="py-16 bg-[#F8FAF9]" style={{ color: '#0D1F17', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif", overflow: 'hidden' }}>
            <div className="max-w-7xl mx-auto px-5 sm:px-8">

                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-12 gap-4 animate-on-scroll anim-in">
                    <div ref={headingRef} className={`section-fade ${headingVisible ? 'visible' : ''}`}>
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-6 h-[1px] bg-teal" />
                            <span className="font-mono-label text-[9px] font-medium text-teal uppercase tracking-[0.5em]">
                                Customer Success
                            </span>
                        </div>
                        <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05]" style={{ color: '#0D1F17' }}>
                            {splitWords('Premium devices for', 100)}{' '}
                            <span className="text-teal inline-block">{splitWords('every use case.', 220)}</span>
                        </h2>
                        
                        {/* Google Rating Badge */}
                        <div className="flex items-center gap-2 mt-5 animate-on-scroll anim-in delay-100">
                            <div className="flex gap-0.5">
                                {[...Array(5)].map((_, i) => (
                                    <svg key={i} width="16" height="16" viewBox="0 0 24 24" fill="#FACC15">
                                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                                    </svg>
                                ))}
                            </div>
                            <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-white border border-evergreen/10 shadow-sm">
                                <img src="https://upload.wikimedia.org/wikipedia/commons/c/c1/Google_%22G%22_logo.svg" alt="Google" className="w-3 h-3" />
                                <span className="text-[11px] font-bold text-[#0D1F17]">4.9 / 5.0</span>
                            </div>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => scrollRail(-1)}
                            className="w-10 h-10 rounded-full border-2 border-evergreen/10 bg-white flex items-center justify-center hover:border-teal hover:bg-teal/10 transition-all"
                            aria-label="Previous testimonial">

                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1E1E1E" strokeWidth="2">
                                <path d="M19 12H5M12 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                        <button
                            onClick={() => scrollRail(1)}
                            className="w-10 h-10 rounded-full bg-teal flex items-center justify-center hover:bg-teal-light transition-all shadow-lg shadow-teal/30"
                            aria-label="Next testimonial">

                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                                <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Rail */}
                <div className="relative animate-on-scroll anim-in delay-100">
                    <div className="absolute left-0 top-0 bottom-0 w-8 sm:w-12 bg-gradient-to-r from-[#F8FAF9] to-transparent z-10 pointer-events-none" />
                    <div className="absolute right-0 top-0 bottom-0 w-8 sm:w-12 bg-gradient-to-l from-[#F8FAF9] to-transparent z-10 pointer-events-none" />

                    <div
                        ref={railRef}
                        className="flex gap-4 sm:gap-5 overflow-x-auto pb-8 pt-4 px-2"
                        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>

                        {testimonials.map((t, i) =>
                            <article
                                key={t.name}
                                className={`w-[85vw] max-w-[320px] sm:max-w-none sm:min-w-[440px] sm:w-[440px] bg-white rounded-3xl border border-evergreen/10 p-6 sm:p-7 hover:-translate-y-1 transition-all duration-400 flex-shrink-0 ${t.rotation}`}
                                style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)' }}>

                                {/* Stars */}
                                <div className="flex gap-0.5 mb-4">
                                    {[...Array(5)].map((_, j) =>
                                        <svg key={j} width="14" height="14" viewBox="0 0 24 24" fill="#FACC15">
                                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                                        </svg>
                                    )}
                                </div>

                                <p className="text-[14px] sm:text-base leading-relaxed mb-6 font-medium" style={{ color: '#0D1F17' }}>
                                    "{t.quote}"
                                </p>

                                {/* Stat badge acts as product tag */}
                                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-teal/10 border border-teal/20 mb-5">
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#2D6A4F" strokeWidth="2.5">
                                        <path d="M22 12h-4l-3 9L9 3l-3 9H2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    <span className="text-[11px] sm:text-xs font-bold text-teal">{t.product}</span>
                                </div>

                                <div className="flex items-center gap-3 pt-4 border-t border-evergreen/10">
                                    <img
                                        src={t.avatar}
                                        alt={`${t.name} - ${t.role}`}
                                        className="w-10 h-10 rounded-full object-cover blur-md" />

                                    <div>
                                        <p className="text-sm font-bold" style={{ color: '#0D1F17' }}>{t.name}</p>
                                        <p className="text-xs" style={{ color: '#4A6B5A' }}>{t.role}</p>
                                    </div>
                                </div>
                            </article>
                        )}
                    </div>
                </div>

                {/* Summary stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mt-8 sm:mt-12 animate-on-scroll anim-in delay-200">
                    {[
                        { value: '10,000+', label: 'Devices Sold' },
                        { value: '4.9 / 5', label: 'Google Rating', isGoogle: true },
                        { value: '98%', label: 'Satisfaction Rate' },
                        { value: '6-Month', label: 'Included Warranty' }
                    ].map(({ value, label, isGoogle }) =>
                        <div key={label} className="bg-white rounded-2xl border border-evergreen/10 p-4 sm:p-5 text-center hover:-translate-y-1 transition-transform relative overflow-hidden group" style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)' }}>
                            {isGoogle && (
                                <div className="absolute top-0 right-0 p-2 opacity-10 group-hover:opacity-20 transition-opacity">
                                    <img src="https://upload.wikimedia.org/wikipedia/commons/c/c1/Google_%22G%22_logo.svg" alt="" className="w-8 h-8" />
                                </div>
                            )}
                            <p className="text-2xl sm:text-3xl font-black text-teal">{value}</p>
                            <p className="text-[10px] sm:text-xs font-semibold mt-1 uppercase tracking-widest flex items-center justify-center gap-1" style={{ color: '#4A6B5A' }}>
                                {label}
                                {isGoogle && <img src="https://upload.wikimedia.org/wikipedia/commons/c/c1/Google_%22G%22_logo.svg" alt="" className="w-3 h-3" />}
                            </p>
                        </div>
                    )}
                </div>

            </div>
        </section>
    );
}
